import React, { ReactNode } from 'react';
import { Box, Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { unpackToDateTime } from '@campfire/hot-date';
import { useCampfireTheme } from '../../../../../theme/useCampfireTheme';
import { StatusChip } from '../../../../../common/chips/StatusChip';
import { BorderLinearProgress } from '../IncomingVolunteersScreenSimple';

const useStyles = makeStyles(() => ({
  listItem: {
    '&:hover': {
      backgroundColor: '#f5f5f5',
      cursor: 'pointer',
    },
    padding: 16,
  },
}));

interface IncomingVolunteerProfileProgram {
  programId: string;
  name: string;
}

interface ReminderType {
  remindedAt: {
    date: string;
  };
}

interface CustomListItemProps {
  name?: string;
  email: string | null;
  avatar: ReactNode;
  entryStatus?: string;
  accountStatus?: string;
  profilePercentageComplete?: number;
  profileCompletionStatus?: string;
  userActivityStatus?: string;
  programs?: IncomingVolunteerProfileProgram[];
  mostRecentReminder?: ReminderType | null;
  onClick?: () => void;
  isAccountRemoved?: boolean;
}

export const CustomListItem = (props: CustomListItemProps) => {
  const {
    name,
    email,
    avatar,
    entryStatus,
    accountStatus,
    profilePercentageComplete,
    profileCompletionStatus,
    userActivityStatus,
    programs,
    onClick,
    mostRecentReminder,
    isAccountRemoved,
  } = props;
  const { theme, isMobile, isMd } = useCampfireTheme();
  const classes = useStyles();
  return (
    <Grid
      item
      container
      direction='row'
      wrap='nowrap'
      alignItems='center'
      className={classes.listItem}
      onClick={() => {
        if (onClick) onClick();
      }}
    >
      <Grid item>{avatar}</Grid>
      <Grid item container direction='column' alignContent='center' style={{ flex: 4 }} spacing={1}>
        <Grid item>
          {name && (
            <Typography component='span' style={{ marginRight: 8 }}>
              {name}
            </Typography>
          )}
          <Typography component='span' variant='body2' color='textSecondary'>
            {email}
          </Typography>
          {programs
            ? programs.map((program) => (
                <Typography
                  key={program.programId}
                  component={isMd ? 'div' : 'span'}
                  variant='body2'
                  color='textSecondary'
                  style={{ marginLeft: isMd ? 0 : 8, marginRight: isMd ? 8 : 0 }}
                >
                  {program.name}
                </Typography>
              ))
            : null}
        </Grid>
        <Grid item container direction='row' alignItems='center' spacing={2}>
          {entryStatus && !isAccountRemoved && (
            <Grid item>
              <StatusChip tiny status={entryStatus} bgcolor={theme.status.green.light} />
            </Grid>
          )}
          {isAccountRemoved && (
            <Grid item>
              <StatusChip tiny status='Application Removed' bgcolor={theme.status.red.medium} />
            </Grid>
          )}
          {accountStatus && (
            <Grid>
              <StatusChip
                tiny
                status={accountStatus}
                bgcolor={accountStatus === 'Account created' ? '#2196F3' : theme.status.red.medium}
              />
            </Grid>
          )}
          {!!profilePercentageComplete && (
            <Grid item style={{ width: 250 }}>
              <BorderLinearProgress variant='determinate' value={profilePercentageComplete * 100} />
            </Grid>
          )}
          {profileCompletionStatus && (
            <Grid item>
              <StatusChip
                tiny
                status={profileCompletionStatus}
                bgcolor={
                  profileCompletionStatus === 'Profile completed' ? theme.palette.primary.main : theme.status.red.medium
                }
              />
            </Grid>
          )}
        </Grid>
      </Grid>
      <Grid item style={{ flex: 1, display: isMobile ? 'none' : 'block' }}>
        <Typography variant='caption' color='textSecondary'>
          {userActivityStatus}
        </Typography>
        {mostRecentReminder && (
          <Box>
            <Typography variant='caption' color='textSecondary'>
              Last reminded {unpackToDateTime(mostRecentReminder.remindedAt).toRelative()}
            </Typography>
          </Box>
        )}
      </Grid>
    </Grid>
  );
};
